import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { reduxForm, arrayPush, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { HotKeys } from 'react-hotkeys';

import _pick from 'lodash/pick';
import _isEqual from 'lodash/isEqual';
import _isEmpty from 'lodash/isEmpty';

import { ruleGroupType } from 'types';
import * as selectors from 'store/services/ruleGroups/selectors';
import { partialExportGroupRule } from 'store/services/rules/actions';
import * as actions from 'store/services/ruleGroups/actions';

import Form from 'components/Form';
import InspectTabs from 'components/InspectTabs';
import InspectButton from 'components/InspectButton';
import InspectFooter from 'components/InspectFooter';
import InspectLoader from 'components/InspectLoader';
import UnsavedChangesModal from 'components/UnsavedChangesModal';

import { setOrderIndex } from 'scenes/Rules/utils';

import FORM_NAMES from 'constants/formNames';
import COMBOS from 'constants/keyboardShortcutsCombos';

import RuleGroupInspectHeader from './components/RuleGroupInspectHeader';
import RuleGroupInspectGeneral from './components/RuleGroupInspectGeneral';
import RuleGroupInspectContext from './components/RuleGroupInspectContext';
import { validate } from './utils';

import './style.scss';

// FIXME: should go to inspect
const GROUP_INSPECT_TABS = [
  { label: 'General', tabIndex: 1, hasWarning: false },
  { label: 'Context Rules', tabIndex: 2, hasWarning: false },
];

const HOTKEYS_MAP = {
  save: COMBOS.saveNew,
  leave: COMBOS.closeInspect,
};

export class RuleGroupsInspect extends Component {
  static propTypes = {
    dirty: PropTypes.bool.isRequired,
    dispatch: PropTypes.func.isRequired,
    groupId: PropTypes.string.isRequired,
    partialExport: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onDeselect: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    change: PropTypes.func,
    currentGroup: ruleGroupType,
    deleteGroup: PropTypes.func,

    // redux-form
    handleSubmit: PropTypes.func,
    isLoading: PropTypes.bool,
    order: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    orderList: PropTypes.arrayOf(
      PropTypes.shape({
        index: PropTypes.number,
        order: PropTypes.string,
      }),
    ),

    selectedRule: PropTypes.bool,
    showUnsavedChangesModal: PropTypes.bool,
    valid: PropTypes.bool,
    onChangeRule: PropTypes.func,
    onCloseUnsavedChangesModal: PropTypes.func,
    onOpenAddRuleModal: PropTypes.func,
  };

  state = {
    rules: this.props.currentGroup.rules,
    isReorderOpened: false,
    isUnsavedChangesModalOpen: false,
    selectedTabIndex: 2,
  };

  componentDidMount = () => {
    const { groupId } = this.props;

    if (groupId) {
      this.props.onSelect(groupId);
    }
  };

  componentWillReceiveProps({ groupId, currentGroup }) {
    if (currentGroup !== this.props.currentGroup) {
      this.setState({
        rules: currentGroup.rules,
      });
    }

    if (this.props.groupId !== groupId) {
      this.props.onSelect(groupId);
    }
  }

  // FIXME: Carefully drop it, check if there are not affects
  shouldComponentUpdate(nextProps, nextState) {
    return (
      nextProps.valid !== this.props.valid ||
      nextProps.dirty !== this.props.dirty ||
      nextProps.isLoading !== this.props.isLoading ||
      nextProps.currentGroup !== this.props.currentGroup ||
      nextProps.order !== this.props.order ||
      nextState.selectedTabIndex !== this.state.selectedTabIndex ||
      nextState.isReorderOpened !== this.state.isReorderOpened ||
      nextState.isUnsavedChangesModalOpen !==
        this.state.isUnsavedChangesModalOpen ||
      !_isEqual(nextState.rules, this.state.rules) ||
      nextState.showUnsavedChangesModal !== this.props.showUnsavedChangesModal
    );
  }

  componentWillUnmount() {
    this.props.onDeselect();
  }

  handleChangeTab = index => {
    this.setState({
      selectedTabIndex: index,
    });
  };

  handleFocusOrder = event => {
    event.target.select();
  };

  handleOpenReorder = e => {
    e.stopPropagation();

    if (this.props.orderList.length > 0) {
      this.setState(({ isReorderOpened }) => ({
        isReorderOpened: !isReorderOpened,
      }));
    }
  };

  handleCloseReorder = () => {
    this.setState({
      isReorderOpened: false,
    });
  };

  handleReorder = index => {
    this.props.change('order', index);
  };

  handleChangeOrder = (e, order) => {
    this.props.change('order', order);
  };

  normalizeOrder = (value, previousValue) => {
    const { orderList } = this.props;
    if (value <= 0) {
      return '';
    }
    if (value <= +orderList[orderList.length - 1].order) {
      return value;
    }

    return previousValue;
  };

  handleDelete = () => {
    this.props.deleteGroup(this.props.currentGroup.id);
    this.props.onClose();
  };

  handleChangeRules = rules => {
    this.setState({
      rules,
    });
  };

  handleClose = () => {
    if (this.props.dirty) {
      this.handleOpenUnsavedChangesModal();
      return;
    }

    this.props.onClose();
  };

  handleSave = () => {
    this.props.dispatch(this.props.handleSubmit(this.props.onSubmit));
  };

  handleOpenUnsavedChangesModal = () => {
    this.setState({
      isUnsavedChangesModalOpen: true,
    });
  };

  handleCloseUnsavedChangesModal = () => {
    this.props.onCloseUnsavedChangesModal();
    this.setState({
      isUnsavedChangesModalOpen: false,
    });
  };

  handleForceCloseInspectorTab = () => {
    this.handleCloseUnsavedChangesModal();
    this.props.onClose(true);
  };

  handleSaveUnsavedChanges = () => {
    this.handleSave();
    this.props.onClose();
  };

  handleDiscardUnsavedChanges = () => {
    const { selectedRule, onChangeRule } = this.props;
    return selectedRule ? onChangeRule() : this.handleForceCloseInspectorTab();
  };

  handlePartialExport = () => {
    this.props.partialExport(`group_rule-${this.props.currentGroup.name}`, [
      this.props.groupId,
    ]);
  };

  keyHandlers = {
    save: this.handleSave,
    leave: this.handleClose,
  };

  renderGeneralTab = () => {
    const { selectedTabIndex } = this.state;
    const {
      orderList,
      order,
      currentGroup: { archived },
    } = this.props;
    const isArchived = Boolean(archived);
    const index = setOrderIndex(orderList, order);
    if (selectedTabIndex !== 1) {
      return null;
    }
    return (
      <RuleGroupInspectGeneral
        index={index}
        isArchived={isArchived}
        isReorderOpened={this.state.isReorderOpened}
        normalizeOrder={this.normalizeOrder}
        order={order}
        orderList={orderList}
        reorderItem={this.handleReorder}
        onChangeOrder={this.handleChangeOrder}
        onClickOrder={this.handleOpenReorder}
        onClose={this.handleCloseReorder}
        onFocusOrder={this.handleFocusOrder}
      />
    );
  };

  renderContextTab = () => {
    const { selectedTabIndex } = this.state;
    const {
      currentGroup,
      currentGroup: { order },
    } = this.props;
    if (selectedTabIndex !== 2 || _isEmpty(currentGroup)) {
      return null;
    }

    return (
      <RuleGroupInspectContext
        groupOrder={order}
        rules={this.state.rules}
        onUpdateRules={this.handleChangeRules}
      />
    );
  };

  renderModals = () => {
    const { isUnsavedChangesModalOpen } = this.state;
    const { showUnsavedChangesModal } = this.props;
    return (
      <React.Fragment>
        {(isUnsavedChangesModalOpen || showUnsavedChangesModal) && (
          <UnsavedChangesModal
            isOpen
            onCancel={this.handleCloseUnsavedChangesModal}
            onDiscard={this.handleDiscardUnsavedChanges}
          />
        )}
      </React.Fragment>
    );
  };

  render() {
    const { selectedTabIndex } = this.state;
    const {
      dirty,
      valid,
      handleSubmit,
      isLoading,
      currentGroup,
      onSubmit,
      onOpenAddRuleModal,
    } = this.props;

    const disabled = !dirty || !valid || isLoading || currentGroup.archived;

    return (
      <HotKeys
        data-test="inspect-group"
        handlers={this.keyHandlers}
        keyMap={HOTKEYS_MAP}
        styleName="inspect"
      >
        {this.renderModals()}

        <InspectTabs
          changeTab={this.handleChangeTab}
          closeTabs={this.handleClose}
          icon="icon-rule-group"
          item={currentGroup}
          selectedTabIndex={selectedTabIndex}
          tabs={GROUP_INSPECT_TABS}
        />

        <RuleGroupInspectHeader
          currentGroup={currentGroup}
          selectedTabIndex={selectedTabIndex}
          onDelete={this.handleDelete}
          onOpenAddRuleModal={onOpenAddRuleModal}
        />

        <div ref={this.handleContainerRef} styleName="wrapper">
          <Form styleName="form" onSubmit={handleSubmit(onSubmit)}>
            <InspectLoader isLoading={isLoading} />
            {this.renderGeneralTab()}
            {this.renderContextTab()}

            <InspectFooter>
              <InspectButton
                isFooter
                data-test="export-entity-button"
                onClick={this.handlePartialExport}
              >
                Export
              </InspectButton>

              <InspectButton
                isFooter
                isSave
                data-test="save-button"
                disabled={disabled}
                type="submit"
              >
                Save
              </InspectButton>
            </InspectFooter>
          </Form>
        </div>
      </HotKeys>
    );
  }
}

const mapStateToProps = state => ({
  order: formValueSelector(FORM_NAMES.GROUPS.INSPECT)(state, 'order'),
  initialValues: {
    ..._pick(selectors.getRuleGroup(state), ['name', 'description']),
    enabled: selectors.getRuleGroupsEnabled(state),
    order: selectors.getRuleGroup(state).order + 1,
  },
  isLoading: selectors.getIsUpdating(state),
});

const mapDispatchToProps = {
  partialExport: partialExportGroupRule,
  deleteGroup: actions.deleteRuleGroup,
  updateGroup: actions.updateRuleGroup,
  addField: arrayPush,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    validate,
    enableReinitialize: true,
    form: FORM_NAMES.GROUPS.INSPECT,
    touchOnBlur: false,
    touchOnChange: true,
  })(RuleGroupsInspect),
);
